export const ARENA_STATS = ["pts", "min", "reb", "ast", "fg3m", "pra"];
// Optional box-score columns: available in the Columns menu, hidden by default.
export const ARENA_EXTRA_STATS = ["tov", "stl", "blk", "oreb", "dreb", "pf", "plus_minus"];
export const ARENA_COLS = [...ARENA_STATS, ...ARENA_EXTRA_STATS];
export const ARENA_LABEL = { pts: "PTS", min: "MIN", reb: "REB", ast: "AST", fg3m: "3PM", pra: "PRA", tov: "TO", stl: "STL", blk: "BLK", oreb: "OREB", dreb: "DREB", pf: "PF", plus_minus: "+/-" };
export const POSITIONS = ["ALL", "G", "F", "C"];
export const WINDOWS = [5, 10, 25];
export const SCOPES = [["overall", "Overall"], ["espn", "ESPN"], ["opponent", "vs Opponent"], ["home", "Home"], ["away", "Away"]];

const livePts = (r) => (Number.isFinite(r.live?.pts) ? r.live.pts : -1);
const byName = (a, b) => a.name.localeCompare(b.name);
// Home team first, then away; inside each team most points in this game first.
const homeThenPts = (a, b) => (b.home === true) - (a.home === true) || livePts(b) - livePts(a) || byName(a, b);

// Confirmed = ESPN's official five for this game (crown). Projected = any expected five we do have —
// NBA.com / RotoWire daily lineups, an admin-set five/override, or the team's previous-game starters —
// shown until ESPN confirms. Anything with no five from any source stays unclassified.
const EXPECTED = new Set(["last_game", "nba", "rotowire", "manual", "override"]);
const confirmed = (r) => r.starter_source === "espn" && r.starter != null;
const projected = (r) => EXPECTED.has(r.starter_source) && r.starter != null;
const classified = (r) => confirmed(r) || projected(r);

export function groupRows(rows) {
  const starters = rows.filter((r) => classified(r) && r.starter).sort(homeThenPts);
  const bench = rows.filter((r) => classified(r) && !r.starter).sort(homeThenPts);
  const unverified = rows.filter((r) => !classified(r)).sort(homeThenPts);
  return { starters, bench, unverified };
}

export const matchesPosition = (r, pos) => pos === "ALL" || (r.position || "").toUpperCase().includes(pos);

// Hit rate for a market: over the player's played recent games, how often the value met the
// current projection line (actual >= rounded projection). Returns {hits, games, pct} or null.
export function playerHit(row, metric) {
  const proj = metric === "min" ? row.min : row.stats?.[metric]?.proj;
  if (proj == null) return null;
  const line = Math.round(proj);
  const games = (row.recent?.games || []).filter((g) => (g.min ?? 0) > 0 && g[metric] != null);
  if (!games.length) return null;
  const hits = games.filter((g) => g[metric] >= line).length;
  return { hits, games: games.length, pct: Math.round((hits / games.length) * 100) };
}

// Pooled hit rate across players and markets (used for the team starters/bench summary).
export function poolHit(rows, metrics) {
  let hits = 0, total = 0;
  for (const r of rows) for (const m of metrics) {
    const h = playerHit(r, m);
    if (h) { hits += h.hits; total += h.games; }
  }
  return total ? Math.round((hits / total) * 100) : null;
}

export function formGames(row, n, scope) {
  const games = (row.recent?.games || []).filter((g) =>
    scope === "overall" || scope === "espn" || (scope === "opponent" ? g.opp === row.opp : scope === "home" ? g.home === true : g.home === false));
  return games.slice(-n);
}

// Short date label, e.g. "28 Oct", for form tooltips.
export const dayLabel = (d) => (d ? new Date(`${d}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" }) : "—");

// Form slots aligned to the team's recent games: a played game, or a {missing:true} placeholder for a
// team game the player did not appear in (DNP or box score not ingested). Only for the default "overall"
// scope and when the team's recent dates are known; otherwise falls back to played games only.
export function formSlots(row, n, scope) {
  const games = formGames(row, n, scope);
  const dates = row.form_team_dates;
  const overall = scope === "overall" || scope === "espn";
  if (!overall || !dates || !dates.length) return games.map((g) => ({ ...g, missing: false }));
  const byDate = {};
  for (const g of row.recent?.games || []) byDate[g.date] = g;
  return dates.slice(-n).map((d) => (byDate[d] ? { ...byDate[d], missing: false } : { date: d, missing: true }));
}

// Live box values must be integers from the provider; anything else is flagged, never rounded.
export function liveCell(row, stat) {
  const v = row.live?.[stat];
  if (v == null) return { text: "—", invalid: false };
  return Number.isInteger(v) ? { text: String(v), invalid: false } : { text: "!", invalid: true, raw: v };
}

// Pace-adjusted read of a live stat: compare the current value with the projection
// scaled by the share of projected minutes already played. tone: over/under/even/null.
// cold = clearly behind pace on a meaningful expectation (used for the underperformer badge).
export function paceTone(row, stat) {
  const now = row.live?.[stat];
  if (!Number.isInteger(now)) return { tone: null, expected: null, cold: false };
  if (stat === "min") {
    const exp = row.min;
    if (exp == null) return { tone: null, expected: null, cold: false };
    return { tone: now > Math.round(exp) ? "over" : "even", expected: exp, cold: false };
  }
  const proj = row.stats?.[stat]?.proj;
  const mins = row.live?.min;
  if (proj == null || !(row.min > 0) || mins == null) return { tone: null, expected: null, cold: false };
  const exp = proj * Math.min(1, mins / row.min);
  const gap = now - exp;
  const band = Math.max(1, exp * 0.15);
  const tone = gap >= band ? "over" : gap <= -band ? "under" : "even";
  const cold = tone === "under" && mins >= 5 && exp >= 3 && now <= exp * 0.6;
  return { tone, expected: exp, cold };
}

// Hover text for a form dot: opponent, date and minutes played.
export const formTip = (g) => {
  const d = g.date ? new Date(`${g.date}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" }) : "—";
  return `${g.result ? `${g.result} · ` : ""}${g.home ? "vs" : "@"} ${g.opp ?? "—"} · ${d} · ${g.min ?? "—"} MIN\n${g.pts ?? "—"} PTS · ${g.reb ?? "—"} REB · ${g.ast ?? "—"} AST · ${g.fg3m ?? "—"} 3PM`;
};

// "RotoBot check" market model for a player + stat, built entirely from the row's recent games.
// Default line = rounded projection; a game "hits" when its value meets or beats the line.
// Pass opts {line, side, p} to compare against a real bookmaker line instead.
export function marketCheck(row, stat, opts = {}) {
  const proj = stat === "min" ? row.min : row.stats?.[stat]?.proj;
  const hasMarket = opts.line != null;
  if (proj == null && !hasMarket) return null;
  const line = hasMarket ? opts.line : Math.round(proj);
  const side = opts.side || null;
  const isHit = (v) => (hasMarket ? (side === "under" ? v < line : v > line) : v >= line);
  const valOf = (g) => (stat === "pra" && g.pra == null ? (g.pts ?? 0) + (g.reb ?? 0) + (g.ast ?? 0) : g[stat]);
  const played = (gs) => gs.filter((g) => (g.min ?? 0) > 0 && valOf(g) != null);
  const last10 = played(formGames(row, 10, "overall")).map((g) => ({
    opp: g.opp, home: g.home, date: g.date, value: valOf(g), hit: isHit(valOf(g)),
  }));
  const seasonGames = played(row.recent?.games || []);
  const seasonHits = seasonGames.filter((g) => isHit(valOf(g))).length;
  const vsGames = played(formGames(row, 99, "opponent"));
  const vsHits = vsGames.filter((g) => isHit(valOf(g))).length;
  const l10hits = last10.filter((g) => g.hit).length;
  const avg10 = last10.length ? Math.round((last10.reduce((a, g) => a + g.value, 0) / last10.length) * 10) / 10 : null;
  const seasonPct = seasonGames.length ? Math.round((seasonHits / seasonGames.length) * 100) : null;
  return {
    stat, proj, line, hasMarket, side,
    chance: hasMarket && opts.p != null ? Math.round(opts.p * 100) : seasonPct,
    last10, l10hits, avg10,
    season: { hits: seasonHits, games: seasonGames.length, pct: seasonPct },
    vsOpp: { hits: vsHits, games: vsGames.length, opp: row.opp },
    direction: hasMarket ? (side === "under" ? "LOWER" : "HIGHER") : (avg10 != null && avg10 >= line ? "HIGHER" : "LOWER"),
  };
}

// Likely defender (two phases, Out players excluded):
//  • Starters guard opposing STARTERS of the same position group (G/F/C), matched by minutes rank
//    within the group (top guard ↔ top guard, 2nd ↔ 2nd …).
//  • Bench/rotation players are matched purely by the team's overall minutes rank — the 6th player
//    by minutes guards the opponent's 6th by minutes, and so on.
const POS_GROUP = { PG: "G", SG: "G", G: "G", SF: "F", PF: "F", F: "F", C: "C" };
export const posGroup = (position) => POS_GROUP[position] || null;
export function likelyDefender(row, rows) {
  if (!row?.position) return null;
  const group = POS_GROUP[row.position];
  if (!group) return null;
  const isOut = (r) => r.injury && /out/i.test(r.injury);
  const byMin = (a, b) => (b.min || 0) - (a.min || 0);
  const game = (rows || []).filter((r) => r.game_id === row.game_id && !isOut(r));
  const oppAll = game.filter((r) => r.team === row.opp).sort(byMin);
  if (!oppAll.length) return null;
  const toDef = (def) => (def ? { name: def.name, position: def.position, player_id: def.player_id, exact: def.position === row.position } : null);

  if (row.starter) {
    const oppStartGroup = oppAll.filter((r) => r.starter && POS_GROUP[r.position] === group);
    const defenders = oppStartGroup.length ? oppStartGroup : oppAll.filter((r) => r.starter);
    if (!defenders.length) return toDef(oppAll[0]);
    const mates = game.filter((r) => r.team === row.team && r.starter && POS_GROUP[r.position] === group).sort(byMin);
    const idx = Math.max(0, mates.findIndex((r) => r.player_id === row.player_id));
    return toDef(defenders[Math.min(idx, defenders.length - 1)]);
  }

  // Bench/rotation: match by overall team minutes rank (6th ↔ 6th).
  const mates = game.filter((r) => r.team === row.team).sort(byMin);
  const idx = Math.max(0, mates.findIndex((r) => r.player_id === row.player_id));
  return toDef(oppAll[Math.min(idx, oppAll.length - 1)]);
}
